import { useMemo, useRef, useState, useEffect } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import api from "@/lib/api";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Global staggered queue – ensures only one Quill instance mounts per tick
let MOUNT_INDEX = 0;

const resolveUrl = (url) => {
    if (!url) return url;
    if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
    return url;
};

export const RichTextEditor = ({ value, onChange, placeholder, testid, allowImages = true }) => {
    const quillRef = useRef(null);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        const myIndex = MOUNT_INDEX++;
        const t = setTimeout(() => setMounted(true), 50 + myIndex * 80);
        return () => clearTimeout(t);
    }, []);

    const imageHandler = () => {
        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("accept", "image/*");
        input.click();
        input.onchange = async () => {
            const file = input.files?.[0];
            if (!file) return;
            const fd = new FormData();
            fd.append("file", file);
            try {
                const { data } = await api.post("/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                const editor = quillRef.current?.getEditor();
                if (!editor) return;
                const range = editor.getSelection(true);
                editor.insertEmbed(range.index, "image", resolveUrl(data.url), "user");
                editor.setSelection(range.index + 1, 0);
                toast.success("Zdjęcie wstawione.");
            } catch (e) {
                toast.error(e.response?.data?.detail || "Nie udało się wgrać zdjęcia.");
            }
        };
    };

    const modules = useMemo(() => {
        const toolbar = [
            [{ header: [2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ align: [] }],
            ["link", "blockquote"],
            [{ color: [] }, { background: [] }],
        ];
        if (allowImages) toolbar.push(["image"]);
        toolbar.push(["clean"]);
        return {
            toolbar: {
                container: toolbar,
                handlers: allowImages ? { image: imageHandler } : {},
            },
            // Don't paste extra <p><br></p> spacers and skip the matchVisual rule that
            // injects &nbsp; when content is copied from Word / Outlook / Google Docs.
            clipboard: { matchVisual: false },
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowImages]);

    return (
        <div data-testid={testid} className="rich-editor bg-white rounded-xl border border-gray-200 overflow-hidden">
            {mounted ? (
                <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={value || ""}
                    onChange={onChange}
                    modules={modules}
                    placeholder={placeholder}
                />
            ) : (
                <div className="p-6 text-sm text-gray-400 min-h-[180px]">Wczytywanie edytora...</div>
            )}
        </div>
    );
};

export default RichTextEditor;
